import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "../../Utils/toastContext";
import { debounce } from "lodash";

const PortfolioBreakdownPopup = ({
  onClose,
  currentInvestmentId,
  setChangeStatus,
  isView,
  fromPartial,
}) => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const location = useLocation();

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [popupData, setPopupData] = useState([]);
  const [popupDataTable, setPopupDataTable] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (fromPartial) {
      getPartialData();
    } else {
      getPopData();
    }
  }, [currentInvestmentId, fromPartial]);
  const formatDate = (dateString) => {
    const [datePart] = dateString.split(" "); // Extract the date portion
    const [day, month, year] = datePart.split("/"); // Split into day, month, and year
    return `${day}/${month}/${year}`;
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem("jwtToken");
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      console.error("API Call Error:", error); // Log full error for debugging
      throw new Error(
        typeof error.message === "string"
          ? error.message
          : "Network error, please try again later"
      );
    }
  };
  const getPopData = async () => {
    setIsLoading(true);
    try {
      const response = await GetAPIcalls(
        `/v1/investment-request/stock-purchases/${currentInvestmentId}`
      );
      if (response.status == 200) {
        setPopupData(response.data);
        setPopupDataTable(response.data.stocks);
      } else {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
        }
        if (response.status === 400) {
          showToast(response.message, "error");
        }
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setTimeout(function () {
        setIsLoading(false);
      }, 100);
    }
  };
  const getPartialData = async () => {
    setIsLoading(true);
    try {
      const response = await GetAPIcalls(
        `/v1/investment-request/stock-purchase/details/${currentInvestmentId}`
      );
      if (response.status == 200) {
        setPopupData(response.data);
        setPopupDataTable(response.data.stocks);
      } else {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
        }
        if (response.status === 400) {
          showToast(response.message, "error");
          setPopupDataTable([]);
          setPopupData(null);
        }
      }
    } catch (err) {
      showToast(err.message, "error");
      setPopupDataTable([]);
      setPopupData(null);
    } finally {
      setTimeout(function () {
        setIsLoading(false);
      }, 100);
    }
  };
  const UpdateStatusCall = async (option) => {
    const token = localStorage.getItem("jwtToken");
    const headers = {
      "Content-Type": "application/json",
    };

    const apiUrl = `${BASE_URL}/v1/investment-request/stock-purchase/status/${currentInvestmentId}`;
    const bodyData = {
      status: option,
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem("jwtToken");
          navigate("/login");
          throw new Error("Session expired. Please log in again.");
        }
        const errorData = await response.json().catch(() => ({
          message: "An unexpected error occurred",
        }));
        throw new Error(errorData.message || "Something went wrong");
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || "Network error, please try again later");
    }
  };

  const handleStatusChange = useCallback(
    debounce(async (e, option) => {
      e.preventDefault();
      let toastMessage = null;

      try {
        const response = await UpdateStatusCall(option);
        if (response.status == 200) {
          setChangeStatus(currentInvestmentId + option);
          onClose();
        } else {
          toastMessage = { message: response.message, type: "error" };
        }
      } catch (err) {
        toastMessage = { message: err.message, type: "error" };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [] // Dependencies
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {!isLoading && (
        <div className="bg-white w-full max-w-4xl p-6 max-h-[90vh]  overflow-y-auto rounded-lg shadow-lg relative">
          {/* Header */}
          <div className="flex justify-between items-center pb-4 mb-4">
            <h2 className="text-xl font-semibold text-center w-full">
              Portfolio Breakdown
            </h2>
            <button
              onClick={onClose}
              className="absolute right-6 text-gray-500 hover:text-black text-lg"
            >
              <img
                src="/add.svg"
                alt="Close"
                className="w-8 h-8 md:w-10 md:h-10"
              />
            </button>
          </div>

          {/* Content */}
          {popupData && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 border rounded p-4">
              {/* Client Information */}
              <div className="md:border-r ">
                <h3 className="font-semibold text-lg mb-2">
                  Client Information
                </h3>
                <div className="space-y-2">
                  <div className="flex flex-col">
                    <label htmlFor="refId" className="text-xs text-[#A6A6A6]">
                      Full Name
                    </label>
                    <span className="text-sm">
                      {popupData.client_information.full_name}
                    </span>
                    <label
                      htmlFor="refId"
                      className="text-xs mt-2 text-[#A6A6A6]"
                    >
                      Client ID
                    </label>
                    <span className="text-sm">
                      {popupData.client_information.client_id}
                    </span>
                    <label
                      htmlFor="refId"
                      className="text-xs mt-2 text-[#A6A6A6]"
                    >
                      Product
                    </label>
                    <span className="text-sm">
                      {popupData.client_information.product_name}
                    </span>
                    <label
                      htmlFor="refId"
                      className="text-xs mt-2 text-[#A6A6A6]"
                    >
                      Cash Account
                    </label>
                    <span className="text-sm">
                      {" "}
                      $
                      {(popupData.client_information.cash_account == null
                        ? 0
                        : popupData.client_information.cash_account
                      )
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                    </span>
                  </div>
                </div>
              </div>

              {/* Portfolio Details */}
              <div>
                <h3 className="font-semibold text-lg mb-2">
                  Portfolio Details
                </h3>
                <div className="space-y-2">
                  <div className="flex flex-col">
                    <label htmlFor="refId" className="text-xs text-[#A6A6A6]">
                      Portfolio ID
                    </label>
                    <span className="text-sm">
                      {popupData.portfolio_details.portfolio_id}
                    </span>
                    <label
                      htmlFor="refId"
                      className="text-xs mt-2 text-[#A6A6A6]"
                    >
                      Total Value
                    </label>
                    <span className="text-sm">
                      {popupData.portfolio_details.amount}
                    </span>
                    <label
                      htmlFor="refId"
                      className="text-xs mt-2 text-[#A6A6A6]"
                    >
                      Number Of Securities
                    </label>
                    <span className="text-sm">
                      {popupData.portfolio_details.no_of_stocks}
                    </span>
                    <label
                      htmlFor="refId"
                      className="text-xs mt-2 text-[#A6A6A6]"
                    >
                      Request Time
                    </label>
                    <span className="text-sm">
                      {formatDate(popupData.portfolio_details.request_time)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {popupDataTable && (
            <div className="overflow-x-auto max-h-[70vh]  font-medium overflow-y-auto mb-6">
              <table className="table-auto w-full border rounded border-gray-300">
                <thead>
                  <tr className="text-[#005A9C] text-left text-xs">
                    <th className="px-4 py-2 border">Security</th>
                    <th className="px-4 py-2 border">Quantity</th>
                    <th className="px-4 py-2 border">Weight%</th>
                    {fromPartial && (
                      <th className="px-4 py-2 border">Purchased Quantity</th>
                    )}
                    <th className="px-4 py-2 border">Price</th>
                    <th className="px-4 py-2 border">Total Value</th>
                    <th className="px-4 py-2 border">Volatility</th>
                  </tr>
                </thead>
                {isLoading ? (
                  <tbody className="shimmer-container-list ">
                    {/* Creating 5 rows of shimmer effect */}
                    {[...Array(5)].map((_, rowIndex) => (
                      <tr key={rowIndex} className="shimmer-row-list">
                        {/* Creating 7 cells in each row for shimmer */}
                        {[...Array(7)].map((_, colIndex) => (
                          <td key={colIndex} className="shimmer-cell-list">
                            <div className="shimmer-loader"></div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                ) : (
                  <tbody className="text-sm">
                    {popupDataTable.map((row, index) => (
                      <tr key={index} className="text-left">
                        <td className="px-4 py-2 border">{row.security}</td>
                        <td className="px-4 py-2 border">
                          {fromPartial ? row.ordered_quantity : row.quantity}
                        </td>
                        <td className="px-4 py-2 border">{row.weight}%</td>
                        {fromPartial && (
                          <td className="px-4 py-2 border">
                            {row.purchased_quantity
                              .toString()
                              .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                          </td>
                        )}
                        <td className="px-4 py-2 border">
                          $
                          {row?.price
                            ? (row.price == null ? 0 : row.price.toFixed(2))
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                            : "0.00"}
                        </td>
                        {!fromPartial && (
                          <td className="px-4 py-2 border">
                            $
                            {row?.total_value
                              ? (row.total_value == null
                                  ? 0
                                  : row.total_value.toFixed(2)
                                )
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                              : "0.00"}
                          </td>
                        )}
                        {fromPartial && (
                          <td className="px-4 py-2 border">
                            $
                            {row?.total_executed_value
                              ? (row.total_executed_value == null
                                  ? 0
                                  : row.total_executed_value.toFixed(2)
                                )
                                  .toString()
                                  .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                              : "0.00"}
                          </td>
                        )}
                        <td className="px-4 py-2 border">{row.volatility}</td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </table>
            </div>
          )}

          {/* Footer */}
          <div className="flex flex-wrap md:flex-nowrap justify-end gap-2">
            {isView && (
              <button
                onClick={() => {
                  let redirectState = {
                    path: "/investment-details-by-id",
                    state: {
                      from: location.pathname,
                      directView: "taa",
                      investment_id: currentInvestmentId,
                      historyStack: [
                        ...(location.state?.historyStack || []),
                        location.pathname,
                      ],
                    },
                  };
                  sessionStorage.setItem(
                    "redirectAfterLogin",
                    JSON.stringify(redirectState)
                  );
                  navigate("/investment-details-by-id", {
                    state: {
                      from: location.pathname,
                      directView: "taa",
                      investment_id: currentInvestmentId,
                      historyStack: [
                        ...(location.state?.historyStack || []),
                        location.pathname,
                      ],
                    },
                  });
                }}
                className="bg-black whitespace-nowrap w-full md:w-auto text-sm text-white px-6 py-3 rounded-full shadow-md hover:bg-gray-800"
              >
                View TAA
              </button>
            )}
            {isView && (
              <button
                onClick={() => {
                  let redirectState = {
                    path: "/investment-details-by-id",
                    state: {
                      from: location.pathname,
                      directView: "daa",
                      investment_id: currentInvestmentId,
                      historyStack: [
                        ...(location.state?.historyStack || []),
                        location.pathname,
                      ],
                    },
                  };
                  sessionStorage.setItem(
                    "redirectAfterLogin",
                    JSON.stringify(redirectState)
                  );
                  navigate("/investment-details-by-id", {
                    state: {
                      from: location.pathname,
                      directView: "daa",
                      investment_id: currentInvestmentId,
                      historyStack: [
                        ...(location.state?.historyStack || []),
                        location.pathname,
                      ],
                    },
                  });
                }}
                className="bg-black whitespace-nowrap w-full md:w-auto text-sm text-white px-6 py-3 rounded-full shadow-md hover:bg-gray-800"
              >
                View DAA
              </button>
            )}
            <button
              onClick={() => alert("Viewing Security Stats")}
              className="bg-black whitespace-nowrap w-full md:w-auto text-sm text-white px-6 py-3 rounded-full shadow-md hover:bg-gray-800"
            >
              View Security Stats
            </button>
          </div>

          {isView && (
            <div className="flex font-semibold mt-4 justify-start">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setLoading(true);
                  handleStatusChange(e, "approved_for_purchase");
                }}
                className={`bg-[#34C046] border-[#34C046] border-2  mr-2 text-sm px-6 py-3 rounded-xl shadow-md flex items-center justify-center space-x-2 ${
                  popupData.status == "approved_for_purchase"
                    ? "bg-opacity-100 text-white "
                    : "bg-opacity-30 text-[#34C046] "
                }`}
              >
                {popupData.status == "approved_for_purchase" && (
                  <img
                    src="/green-selected.svg"
                    alt="Company Logo"
                    className="w-3 h-3 md:w-4 md:h-4"
                  />
                )}
                {popupData.status != "approved_for_purchase" && (
                  <img
                    src="/green-unselected.svg"
                    alt="Company Logo"
                    className="w-3 h-3 md:w-4 md:h-4"
                  />
                )}
                <span className="whitespace-nowrap">Approved for Trade</span>
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  setLoading(true);
                  handleStatusChange(e, "rejected");
                }}
                className={`bg-[#FF5050] border-[#FF5050] border-2  mr-2 text-sm px-6 py-3 rounded-xl shadow-md flex items-center justify-center space-x-2 ${
                  popupData.status == "rejected"
                    ? "bg-opacity-100 text-white "
                    : "bg-opacity-30 text-[#FF5050] "
                }`}
              >
                {popupData.status == "rejected" && (
                  <img
                    src="/red-selected.svg"
                    alt="Company Logo"
                    className="w-3 h-3 md:w-4 md:h-4"
                  />
                )}
                {popupData.status != "rejected" && (
                  <img
                    src="/red-unselected.svg"
                    alt="Company Logo"
                    className="w-3 h-3 md:w-4 md:h-4"
                  />
                )}
                <span className="whitespace-nowrap">REJECT</span>
              </button>
            </div>
          )}
        </div>
      )}
      {isLoading && (
        <div className="bg-white w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto rounded-lg shadow-lg relative animate-pulse">
          {/* Shimmer Header */}
          <div className="flex justify-between items-center pb-4 mb-4">
            <div className="w-1/3 h-6 bg-gray-300 rounded"></div>
            <div className="w-10 h-10 bg-gray-300 rounded-full"></div>
          </div>

          {/* Shimmer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6 border rounded p-4">
            {/* Shimmer Client Info */}
            <div className="space-y-3">
              <div className="h-5 bg-gray-300 w-1/2 rounded"></div>
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-300 w-3/4 rounded"></div>
              ))}
            </div>

            {/* Shimmer Portfolio Details */}
            <div className="space-y-3">
              <div className="h-5 bg-gray-300 w-1/2 rounded"></div>
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-4 bg-gray-300 w-3/4 rounded"></div>
              ))}
            </div>
          </div>

          {/* Shimmer Table */}
          <div className="overflow-x-auto max-h-[70vh] font-medium overflow-y-auto mb-6">
            <table className="table-auto w-full border rounded border-gray-300">
              <thead>
                <tr className="text-left text-xs">
                  {[...Array(6)].map((_, i) => (
                    <th key={i} className="px-4 py-2 border bg-gray-200"></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...Array(5)].map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {[...Array(6)].map((_, colIndex) => (
                      <td key={colIndex} className="px-4 py-2 border">
                        <div className="h-4 bg-gray-300 rounded"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioBreakdownPopup;
